import { Component } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RadioComponent, TextInputComponent, DateInputComponent, FileTextUploadComponent, TextAreaComponent } from 'ddmo-storybook-lib';
import { WizardNavigationComponent } from '../../../../shared/components/wizard-navigation/wizard-navigation.component';
import { DentalCredentialsService } from '../../services/dental-credentials.service';
import { DocumentCategory, DocumentSubCategory, DocumentSubCategoryName, Specialities } from '../../../../shared/constants/document.constants';
import { v4 as uuidv4 } from 'uuid';
import { SharedService } from '../../../../shared/services/shared.service';
import { LoaderService } from '../../../../shared/services/loader.service';
import { PopupComponent } from '../popup/popup.component';
import { MatDialog } from '@angular/material/dialog';

export interface CredentialDocument {
  name: string;
  originalFileName?: string;
  url?: string;
  uploadedAt?: Date;
  type?: string;
  file?: File;
  isSigned?: boolean;
}

export function currencyRangeValidator(min?: number, max?: number) {
  return (control: AbstractControl) => {
    if (!control.value) return null;

    const numericValue = Number(
      control.value.toString().replace(/[$,]/g, '')
    );

    if (isNaN(numericValue)) return null;

    if (min !== undefined && numericValue < min) {
      return { min: { min, actual: numericValue } };
    }

    if (max !== undefined && numericValue > max) {
      return { max: { max, actual: numericValue } };
    }

    return null;
  };
}
const CONTROLLED_SUBSTANCE_MIN_DAYS_FROM_SUBMISSION = 30;
const CONTROLLED_SUBSTANCE_EXPIRATION_MESSAGE = 'Expiration date must be 30 days from date of submission';

export function minDaysFromSubmissionValidator(minDays: number) {
  return (control: AbstractControl) => {
    if (!control.value) return null;

    const submissionDate = new Date();
    submissionDate.setHours(0, 0, 0, 0);

    const minimumAllowedDate = new Date(submissionDate);
    minimumAllowedDate.setDate(minimumAllowedDate.getDate() + minDays);

    const inputDate = new Date(control.value);
    inputDate.setHours(0, 0, 0, 0);
    if (isNaN(inputDate.getTime())) return null;

    return inputDate < minimumAllowedDate ? { minDaysFromSubmission: true } : null;
  };
}
export function minDaysOutDateValidator(minDaysFromToday: number) {
  return (control: AbstractControl) => {
    if (!control.value) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const minimumAllowedDate = new Date(today);
    minimumAllowedDate.setDate(minimumAllowedDate.getDate() + minDaysFromToday);

    const inputDate = new Date(control.value);
    inputDate.setHours(0, 0, 0, 0);
    if (isNaN(inputDate.getTime())) return null;

    return inputDate < minimumAllowedDate
      ? { minDaysOut: { minDays: minDaysFromToday } }
      : null;
  };
}
export function pastOrTodayDateValidator() {
  return (control: AbstractControl) => {
    if (!control.value) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const inputDate = new Date(control.value);
    if (isNaN(inputDate.getTime())) return null;

    return inputDate > today ? { futureDate: true } : null;
  };
}
@Component({
  selector: 'app-dental-credentials',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    WizardNavigationComponent,
    RadioComponent,
    TextInputComponent,
    DateInputComponent,
    FileTextUploadComponent,
    TextAreaComponent,
  ],
  templateUrl: './dental-credentials.component.html',
  styleUrl: './dental-credentials.component.scss'
})
export class DentalCredentialsComponent {
  private readonly MIN_EXPIRATION_DAYS_FROM_SUBMISSION = CONTROLLED_SUBSTANCE_MIN_DAYS_FROM_SUBMISSION;
  private readonly PARTICIPATING_AGREEMENT_RESIGN_MESSAGE = 'Please navigate to the documents tab and read and sign the participating agreement.';
  MAX_DOCS = 3;

  documentsMap: Record<string, CredentialDocument[]> = {
    STATE_LICENSE: [],
    CDS: [],
    DEA: [],
    MALPRACTICE: [],
    SPECIALTY: [],
    ANESTHESIA: [],
    ADDITIONAL1: [],
    ADDITIONAL2: [],
    ADDITIONAL3: [],
  };

  dentistCredentialsForm: FormGroup;

  cdsradioOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  dEALicenseradioOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ];

  policyTypeRadioOptions = [
    { value: 'claimsMade', label: 'Claims Made' },
    { value: 'occurrence', label: 'Occurrence' }
  ];

  cdsForm = this.fb.group({ cds: ['', Validators.required] });
  deaLicenseForm = this.fb.group({ dea: ['', Validators.required] });
  policyTypeForm = this.fb.group({ malpracticePolicyType: ['', Validators.required] });
  credentialPayload: any;

  VALIDATION_MESSAGES: any = {
    stateLicenseNumber: {
      required: 'Required',
      pattern: JSON.parse(localStorage.getItem('loggedUserDetails')!)['licenseState'] == 'SC'
        ? 'Must be 4–5 digits only' : 'Must be 5–10 digits only',
      patternDefault: 'Must be 4–10 digits only'
    },

    cdsLicenseNumber: {
      required: 'Required',
      pattern: 'Must between 1 to 10 alphanumeric characters or hyphens',
      minlength: 'Must be exactly 1 characters',
      maxlength: 'Must be exactly 10 characters'
    },

    deaLicenseNumber: {
      required: 'Required',
      pattern: 'Must start with 2 letters followed by 7 digits',
      minlength: 'Must be exactly 9 characters',
      maxlength: 'Must be exactly 9 characters'
    },

    painManagementNote: {
      required: 'Required',
      minlength: 'Must contain at least 5 characters',
      maxlength: 'Cannot exceed 250 characters'
    },

    specialtyLicenseNumber: {
      pattern: 'Format is invalid',
      minlength: 'Must be at least 1 character',
      maxlength: 'Cannot exceed 10 characters'
    },

    anesthesiaSedationLicense: {
      pattern: 'Format is invalid'
    },

    malpracticeCarrierName: {
      required: 'Required',
      pattern: 'Can contain letters, numbers and spaces only',
      minlength: 'Must be at least 1 character',
      maxlength: 'Cannot exceed 80 characters',
      max: 'Cannot exceed 80 characters'
    },

    malpracticePolicyHolder: {
      required: 'Required',
      pattern: 'Can contain letters, numbers, spaces and valid symbols only',
      minlength: 'Must be at least 1 character',
      maxlength: 'Cannot exceed 37 characters'
    },

    malpracticeLimitPerClaim: {
      required: 'Required',
      minlength: 'Must be at least 1 character',
      maxlength: 'Cannot exceed $1,000,000,000',
      max: 'Cannot exceed $1,000,000,000',
      min: 'Must be greater than $0',
      pattern: 'Must be a valid numeric amount'
    },

    malpracticeLimitAggregate: {
      required: 'Required',
      minlength: 'Must be at least 1 character',
      maxlength: 'Cannot exceed 1,000,000,000',
      min: 'Must be greater than $0',
      max: 'Cannot exceed $1,000,000,000',
      pattern: 'Must be a valid numeric amount'
    },

    malpracticeExpirationDate: {
      required: 'Required',
      pattern: 'Enter a valid expiration date',
      minDaysOut: 'Expiration date must be at least 30 days from submission'
    },

    stateLicenseEffectiveDate: {
      required: 'Required',
      pattern: 'Enter a valid date',
      futureDate: 'Effective date cannot be in the future'
    },

    dEAExpirationDate: {
      required: 'Required',
      pattern: 'Enter a valid expiration date',
      minDaysFromSubmission: CONTROLLED_SUBSTANCE_EXPIRATION_MESSAGE
    },

    cDSExpirationDate: {
      required: 'Required',
      pattern: 'Enter a valid expiration date',
      minDaysFromSubmission: CONTROLLED_SUBSTANCE_EXPIRATION_MESSAGE
    },

    specialtyLicenseEffectiveDate: {
      required: 'Required',
      pattern: 'Enter a valid date',
    },

    anesthesiaEffectiveDate: {
      required: 'Required',
      pattern: 'Enter a valid date'
    },
  };
  isSpecialityLicenceNumberRequired = false;
  minExpirationDate: any = new Date();
  maxExpirationDate: any = '12/31/9999';
  maxStateLicenseEffectiveDate: Date = new Date(new Date().setHours(0, 0, 0, 0));
  impersonatedUser: any = localStorage.getItem("impersonatedUser") ?? ""
  FIELD_LIMITS: any = {
    stateLicenseNumber: { type: 'string', maxLength: 10 },
    cdsLicenseNumber: { type: 'string', maxLength: 10 },
    deaLicenseNumber: { type: 'string', maxLength: 9 },
    painManagementNote: { type: 'string', maxLength: 200 },
    specialtyLicenseNumber: { type: 'string', maxLength: 10 },
    anesthesiaSedationLicense: { type: 'string', maxLength: 50 },
    malpracticeCarrierName: { type: 'string', maxLength: 80 },
    malpracticePolicyHolder: { type: 'string', maxLength: 37 },
    malpracticeLimitPerClaim: {
      type: 'number',
      maxValue: 1000000000.00,
      precision: 2,
      ignoreSymbols: true
    },
    malpracticeLimitAggregate: {
      type: 'number',
      maxValue: 1000000000.00,
      precision: 2,
      ignoreSymbols: true
    }
  }
  constructor(private fb: FormBuilder, private router: Router, private dentalCredentialingService: DentalCredentialsService,
    private sharedService: SharedService, private route: ActivatedRoute, private loaderService: LoaderService,
    private dialog: MatDialog, private datePipe: DatePipe,
  ) {
    this.dentistCredentialsForm = this.fb.group({
      credentialID: [''],
      stateLicenseNumber: [''],
      cdsLicenseNumber: ['', [Validators.pattern(/^[A-Za-z0-9-]{1,10}$/)]],
      deaLicenseNumber: ['', [Validators.pattern(/^[A-Za-z]{2}-?[0-9]{7}$/)]],
      painManagementNote: [''],
      specialtyLicenseNumber: ['', [Validators.pattern(/^[A-Za-z0-9]{0,10}$/)]],
      anesthesiaSedationLicense: ['', []],
      malpracticeCarrierName: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9\s]{1,80}$/)]],
      malpracticePolicyHolder: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9\s./-]{1,37}$/)]],
      malpracticeLimitPerClaim: ['', [Validators.required, currencyRangeValidator(0.01, 1000000000.00)]],
      malpracticeLimitAggregate: ['', [Validators.required, currencyRangeValidator(0.01, 1000000000.00)]],
      malpracticeExpirationDate: ['', [Validators.required, minDaysOutDateValidator(this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)]],
      stateLicenseEffectiveDate: ['', [Validators.required, pastOrTodayDateValidator()]],
      cDSExpirationDate: [''],
      dEAExpirationDate: [''],
      specialtyLicenseEffectiveDate: [''],
      anesthesiaEffectiveDate: [''],
    });
    this.route.queryParams.subscribe((params: any) => {
      this.minExpirationDate = new Date();
      this.credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
      const userDetails: any = JSON.parse(localStorage.getItem('loggedUserDetails')!);
      const statelicenseNumberCtrl = this.dentistCredentialsForm.get('stateLicenseNumber') as FormControl;
      const stateLicenseEffectiveDateCtrl = this.dentistCredentialsForm.get('stateLicenseEffectiveDate') as FormControl;
      const cdsLicenseNumberCtrl = this.dentistCredentialsForm.get('cdsLicenseNumber') as FormControl;
      const cdsExpirationDateCtrl = this.dentistCredentialsForm.get('cDSExpirationDate') as FormControl;
      const deaLicenseNumberCtrl = this.dentistCredentialsForm.get('deaLicenseNumber') as FormControl;
      const deaExpirationDateCtrl = this.dentistCredentialsForm.get('dEAExpirationDate') as FormControl;
      const credential = JSON.parse(localStorage.getItem('credential')!) || [];
      const documents = JSON.parse(localStorage.getItem('documents') || '[]');
      const dentistInfo = JSON.parse(localStorage.getItem('dentistInfo') || '{}');
      const isRecred = this.getAction();
      const hasDocuments = Array.isArray(documents) && documents.length > 0;
      const isCredentialSeedOnly = credential?.length === 1
        && Object.keys(credential[0] || {}).length === 1
        && Object.prototype.hasOwnProperty.call(credential[0] || {}, 'stateLicenseNumber');
      const hasCredentialData = Array.isArray(credential)
        && credential.length > 0
        && !isCredentialSeedOnly;

      const getDentistInfoValue = (...keys: string[]) => {
        for (const key of keys) {
          const value = dentistInfo?.[key];
          if (value !== undefined && value !== null && `${value}`.trim() !== '') {
            return value;
          }
        }
        return '';
      };

      const resolvedStateLicenseNumber = getDentistInfoValue(
        'licenseNumber',
        'stateLicenseNumber',
        'licensenumber',
        'statelicensenumber'
      );
      const resolvedStateLicenseEffectiveDate = this.normalizeDate(getDentistInfoValue(
        'licenseEffectiveDate',
        'stateLicenseEffectiveDate',
        'licenseeffectivedate',
        'statelicenseeffectivedate'
      )) || null;
      const resolvedCdsLicense = getDentistInfoValue(
        'cdsLicense',
        'cDSLicense',
        'cdslicense'
      );
      const resolvedCdsLicenseExpirationDate = this.normalizeDate(getDentistInfoValue(
        'cdsLicenseExpirationDate',
        'cDSLicenseExpirationDate',
        'cdslicenseexpirationdate'
      )) || null;
      const resolvedDeaLicense = getDentistInfoValue(
        'deaLicense',
        'dEALicense',
        'dealicense'
      );
      const resolvedDeaLicenseExpirationDate = this.normalizeDate(getDentistInfoValue(
        'deaLicenseExpirationDate',
        'dEALicenseExpirationDate',
        'dealicenseexpirationdate'
      )) || null;
      if (hasCredentialData) {
        const payload = {
          credentials: credential,
          documents
        }
        this.patchValues(payload);
      } else if (isRecred && ((!credential?.length) || isCredentialSeedOnly)) {

        const stateLicenseNumber = resolvedStateLicenseNumber || userDetails?.licenseNumber || '';
        const cdsLicense = resolvedCdsLicense;
        const cdsLicenseExpirationDate = resolvedCdsLicenseExpirationDate;
        const deaLicense = resolvedDeaLicense;
        const deaLicenseExpirationDate = resolvedDeaLicenseExpirationDate;

        statelicenseNumberCtrl.setValue(stateLicenseNumber);

        cdsLicenseNumberCtrl.setValue(cdsLicense);
        cdsExpirationDateCtrl.setValue(cdsLicenseExpirationDate);
        this.cdsForm.patchValue({ cds: cdsLicense || cdsLicenseExpirationDate ? 'true' : 'false' }, { emitEvent: false });

        deaLicenseNumberCtrl.setValue(deaLicense);
        deaExpirationDateCtrl.setValue(deaLicenseExpirationDate);
        this.deaLicenseForm.patchValue({ dea: deaLicense || deaLicenseExpirationDate ? 'true' : 'false' }, { emitEvent: false });

        statelicenseNumberCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        stateLicenseEffectiveDateCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        cdsExpirationDateCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        deaExpirationDateCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        cdsExpirationDateCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        deaExpirationDateCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        cdsLicenseNumberCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        cdsExpirationDateCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        deaLicenseNumberCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        deaExpirationDateCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
        this.cdsForm.updateValueAndValidity({ emitEvent: false });
        this.deaLicenseForm.updateValueAndValidity({ emitEvent: false });
        this.dentistCredentialsForm.updateValueAndValidity({ emitEvent: false });

        if (hasDocuments) {
          this.patchValues({
            credentials: [],
            documents
          });
        }
      } else if (!isRecred && ((!credential?.length) || isCredentialSeedOnly)) {
        const stateLicenseNumber = resolvedStateLicenseNumber || userDetails?.licenseNumber || userDetails?.stateLicenseNumber || '';
        const cdsLicense = resolvedCdsLicense;
        const cdsLicenseExpirationDate = resolvedCdsLicenseExpirationDate;
        const deaLicense = resolvedDeaLicense;
        const deaLicenseExpirationDate = resolvedDeaLicenseExpirationDate;

        statelicenseNumberCtrl.setValue(stateLicenseNumber);

        cdsLicenseNumberCtrl.setValue(cdsLicense);
        cdsExpirationDateCtrl.setValue(cdsLicenseExpirationDate);
        this.cdsForm.patchValue({ cds: cdsLicense || cdsLicenseExpirationDate ? 'true' : 'false' }, { emitEvent: false });

        deaLicenseNumberCtrl.setValue(deaLicense);
        deaExpirationDateCtrl.setValue(deaLicenseExpirationDate);
        this.deaLicenseForm.patchValue({ dea: deaLicense || deaLicenseExpirationDate ? 'true' : 'false' }, { emitEvent: false });
      }
      // Disable or enable state license fields based on existing data from dentistInfo
      const dwStateLicenseNumber = dentistInfo?.licenseNumber || null;
      const dwStateLicenseEffectiveDate = this.normalizeDate(dentistInfo?.licenseEffectiveDate) || null;
      

      if (dwStateLicenseNumber) {
        statelicenseNumberCtrl.setValue(dwStateLicenseNumber, { emitEvent: false });
        statelicenseNumberCtrl.disable({ emitEvent: false });
      } else {
        statelicenseNumberCtrl.enable({ emitEvent: false });
      }
      if (dwStateLicenseEffectiveDate) {
        stateLicenseEffectiveDateCtrl.setValue(dwStateLicenseEffectiveDate, { emitEvent: false });
        stateLicenseEffectiveDateCtrl.disable({ emitEvent: false });
      } else {
        stateLicenseEffectiveDateCtrl.enable({ emitEvent: false });
      }

      const dwCdsLicense = (resolvedCdsLicense || '').toString().trim();
      const dwCdsExpirationDate = resolvedCdsLicenseExpirationDate || null;

      const hasExistingCdsValue = this.hasMeaningfulValue(cdsLicenseNumberCtrl.value) || this.hasMeaningfulValue(cdsExpirationDateCtrl.value);

      if (!hasExistingCdsValue) {
        if (dwCdsLicense) {
          cdsLicenseNumberCtrl.setValue(dwCdsLicense, { emitEvent: false });
        }
        if (dwCdsExpirationDate) {
          cdsExpirationDateCtrl.setValue(dwCdsExpirationDate, { emitEvent: false });
        }
      }
      cdsLicenseNumberCtrl.enable({ emitEvent: false });
      cdsExpirationDateCtrl.enable({ emitEvent: false });
      if (!hasExistingCdsValue && (dwCdsLicense || dwCdsExpirationDate)) {
        this.cdsForm.patchValue({ cds: 'true' }, { emitEvent: false });
      }

      const dwDeaLicense = (resolvedDeaLicense || '').toString().trim();
      const dwDeaExpirationDate = resolvedDeaLicenseExpirationDate || null;

      const hasExistingDeaValue = this.hasMeaningfulValue(deaLicenseNumberCtrl.value) || this.hasMeaningfulValue(deaExpirationDateCtrl.value);

      if (!hasExistingDeaValue) {
        if (dwDeaLicense) {
          deaLicenseNumberCtrl.setValue(dwDeaLicense, { emitEvent: false });
        }
        if (dwDeaExpirationDate) {
          deaExpirationDateCtrl.setValue(dwDeaExpirationDate, { emitEvent: false });
        }
      }
      deaLicenseNumberCtrl.enable({ emitEvent: false });
      deaExpirationDateCtrl.enable({ emitEvent: false });
      if (!hasExistingDeaValue && (dwDeaLicense || dwDeaExpirationDate)) {
        this.deaLicenseForm.patchValue({ dea: 'true' }, { emitEvent: false });
      }
      this.syncControlledSubstanceValidators();

      if (Object.keys(userDetails).length) {
        if (userDetails.licenseState == 'SC') {
          statelicenseNumberCtrl?.setValidators([
            Validators.required,
            Validators.pattern(/^[0-9]{4,5}$/)
          ]);
          this.FIELD_LIMITS['stateLicenseNumber']['maxLength'] = 5;
          let demographics = JSON.parse(localStorage.getItem('demographics')!);
          const specialtyLicenseNumberCtrl = this.dentistCredentialsForm.get('specialtyLicenseNumber') as FormControl;
          const sLNEffDateCtrl = this.dentistCredentialsForm.get('specialtyLicenseEffectiveDate') as FormControl;
          if (demographics['specialty'] && demographics['specialty'].toString() !== Specialities.GeneralDentistry.toString()) { // validating general dentistry
            specialtyLicenseNumberCtrl?.setValidators([
              Validators.required,
              Validators.pattern(/^[A-Za-z0-9]{1,10}$/)
            ]);
            specialtyLicenseNumberCtrl?.markAsTouched();
            sLNEffDateCtrl?.setValidators([
              Validators.required,
            ])
            sLNEffDateCtrl?.markAsTouched();
            this.FIELD_LIMITS['specialtyLicenseNumber']['maxLength'] = 10;
            this.VALIDATION_MESSAGES['specialtyLicenseNumber']['required'] = 'Required';
            this.VALIDATION_MESSAGES['specialtyLicenseNumber']['pattern'] = 'Must be 1 to 10 alphanumeric characters';
            this.isSpecialityLicenceNumberRequired = true;
          } else {
            specialtyLicenseNumberCtrl.clearValidators();
            specialtyLicenseNumberCtrl.setErrors(null);
            specialtyLicenseNumberCtrl?.setValidators([
              Validators.pattern(/^[A-Za-z0-9]{0,10}$/)
            ]);
            specialtyLicenseNumberCtrl?.markAsUntouched();
            if (!specialtyLicenseNumberCtrl?.value) {
              sLNEffDateCtrl?.markAsUntouched();
              sLNEffDateCtrl.clearValidators();
              sLNEffDateCtrl.setErrors(null);
            }
            this.isSpecialityLicenceNumberRequired = false;
            this.VALIDATION_MESSAGES['specialtyLicenseNumber'] = {
              pattern: 'Must be 1 to 10 alphanumeric characters',
              minlength: 'Must be at least 1 character',
              maxlength: 'Cannot exceed 10 characters',
            }
          }
          specialtyLicenseNumberCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
          sLNEffDateCtrl.updateValueAndValidity({ onlySelf: true, emitEvent: true });
        } else {
          statelicenseNumberCtrl?.setValidators([
            Validators.required,
            Validators.pattern(/^[0-9]{5,10}$/)
          ]);
          this.FIELD_LIMITS['stateLicenseNumber']['maxLength'] = 10;
        }
      } else {
        statelicenseNumberCtrl?.setValidators([
          Validators.required,
          Validators.pattern(/^[0-9]{4,10}$/)
        ]);
        this.FIELD_LIMITS['stateLicenseNumber']['maxLength'] = 10;
      }
      statelicenseNumberCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    });

  }

  ngOnInit() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.cdsForm.get('cds')?.valueChanges.subscribe(async (value) => {
      const cdsCtrl = this.dentistCredentialsForm.get('cdsLicenseNumber') as FormControl;
      const cdsExpDateCtrl = this.dentistCredentialsForm.get('cDSExpirationDate') as FormControl;
      if (value === 'true') {
        cdsCtrl?.setValidators([
          Validators.required,
          Validators.pattern(/^[A-Za-z0-9-]{1,10}$/)
        ]);
        cdsExpDateCtrl?.setValidators([
          Validators.required, minDaysFromSubmissionValidator(this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)
        ]);
        cdsCtrl?.markAsTouched();
        cdsExpDateCtrl?.markAsTouched();
      } else {
        if (this.documentsMap['CDS']?.length)
          await this.onDeleteDocument('CDS', 0);
        cdsCtrl.setValue('');
        cdsCtrl?.clearValidators();
        cdsCtrl?.setErrors(null);
        cdsCtrl?.markAsUntouched();
        cdsExpDateCtrl.setValue('');
        cdsExpDateCtrl?.clearValidators();
        cdsExpDateCtrl?.setErrors(null);
        cdsExpDateCtrl?.markAsUntouched();
      }
      cdsCtrl?.updateValueAndValidity();
      cdsCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      cdsExpDateCtrl?.updateValueAndValidity();
      cdsExpDateCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      this.applyFinalValidations(value, 'CDS', this.deaLicenseForm.value.dea);
    });

    this.deaLicenseForm.get('dea')?.valueChanges.subscribe(async (value) => {
      const deaCtrl = this.dentistCredentialsForm.get('deaLicenseNumber') as FormControl;
      const deaExpDateCtrl = this.dentistCredentialsForm.get('dEAExpirationDate') as FormControl;
      if (value === 'true') {
        deaCtrl?.setValidators([
          Validators.required,
          Validators.pattern(/^[A-Za-z]{2}[0-9]{7}$/)
        ]);
        deaExpDateCtrl?.setValidators([
          Validators.required, minDaysFromSubmissionValidator(this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)
        ]);
        deaCtrl?.markAsTouched();
        deaExpDateCtrl?.markAsTouched();
      } else {
        if (this.documentsMap['DEA']?.length)
          await this.onDeleteDocument('DEA', 0);
        deaCtrl.setValue('');
        deaCtrl?.clearValidators();
        deaCtrl?.setErrors(null);
        deaCtrl?.markAsUntouched();
        deaExpDateCtrl.setValue('');
        deaExpDateCtrl?.clearValidators();
        deaExpDateCtrl?.setErrors(null);
        deaExpDateCtrl?.markAsUntouched();
      }
      deaCtrl?.updateValueAndValidity();
      deaCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      deaExpDateCtrl?.updateValueAndValidity();
      deaExpDateCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: true });
      this.applyFinalValidations(value, 'DEA', this.cdsForm.value.cds);
    });
    this.dentistCredentialsForm.get('specialtyLicenseNumber')?.valueChanges.subscribe(async (value) => {
      const sLNEffDateCtrl = this.dentistCredentialsForm.get('specialtyLicenseEffectiveDate') as FormControl;
      if (value) {
        sLNEffDateCtrl?.setValidators([
          Validators.required,
        ]);
        sLNEffDateCtrl?.markAsTouched();
      } else {
        sLNEffDateCtrl.setValue('');
        sLNEffDateCtrl?.clearValidators();
        sLNEffDateCtrl?.setErrors(null);
        sLNEffDateCtrl?.markAsUntouched();
      }
      sLNEffDateCtrl.updateValueAndValidity();
    });
    this.dentistCredentialsForm.get('anesthesiaSedationLicense')?.valueChanges.subscribe(async (value) => {
      const aSLNEffDateCtrl = this.dentistCredentialsForm.get('anesthesiaEffectiveDate') as FormControl;
      if (value) {
        aSLNEffDateCtrl?.setValidators([
          Validators.required,
        ]);
        aSLNEffDateCtrl?.markAsTouched();
      } else {
        aSLNEffDateCtrl.setValue('');
        aSLNEffDateCtrl?.clearValidators();
        aSLNEffDateCtrl?.setErrors(null);
        aSLNEffDateCtrl?.markAsUntouched();
      }
      aSLNEffDateCtrl.updateValueAndValidity();
    });
    if (this.impersonatedUser) {
      this.dentistCredentialsForm.disable();
      this.cdsForm.disable();
      this.deaLicenseForm.disable();
      this.policyTypeForm.disable();
    }
  }

  async onUploadDocument(event: { file: File; type: string }) {
    const { file, type } = event;
    this.credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
    this.documentsMap[type] ??= [];

    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const uniqueId = uuidv4();
    const ext = file.name.split('.').pop();
    if (!ext?.includes('pdf')) {
      const alertMessage = 'Only PDF is accepted.';
      const dialogRef = this.dialog.open(PopupComponent, {
        disableClose: true,
        data: {
          headerMessage: 'Alert',
          message: alertMessage, isPTESaved: true
        },
        width: '550px',
        backdropClass: '',
        hasBackdrop: true,
      });
      return;
    }
    const filename = `${year}${month}${day}_${this.credentialPayload['npi']}_${uniqueId}`;

    try {
      const response: any = await this.saveDocument(file, type, filename);

      const documents = JSON.parse(localStorage.getItem('documents') || '[]');
      const subCategoryId = this.getSubCategory(type);

      const payload = {
        documentId: response.documentId,
        category: 'DentalCredential',
        categoryId: DocumentCategory.DentalCredential,
        credentialingEventId: localStorage.getItem('credentialingEventId'),
        fileName: filename,
        isSigned: false,
        subCategoryId,
        subcategory: DocumentSubCategoryName[subCategoryId]
      };

      this.insertDocumentMap(payload);

      const index = documents.findIndex(
        (item: any) => item.subCategoryId === subCategoryId
      );

      if (index !== -1) {
        documents.splice(index, 1);
      }

      documents.push(payload);
      localStorage.setItem('documents', JSON.stringify(documents));

    } catch (error) {
      this.sharedService.logError("APIFailed", "FileDental-CredentialsComponent - saveDocument, Method:onUploadDocument", this.router.url.split('?')[0], error).subscribe();
    }
  }

  getSubCategory(type: any) {
    const sub_category = type == 'STATE_LICENSE' ?
      DocumentSubCategory.StateLicense :
      type == 'CDS' ? DocumentSubCategory.CDSLicense :
        type == 'DEA' ? DocumentSubCategory.DEALicense :
          type == 'MALPRACTICE' ? DocumentSubCategory.MalpracticeInsuranceLicense :
            type == 'SPECIALTY' ? DocumentSubCategory.SpecialtyLicense :
              type == 'ANESTHESIA' ? DocumentSubCategory.AnesthesiaSedationLicense :
                type == 'ADDITIONAL1' ? DocumentSubCategory.AdditionalDocument1 :
                  type == 'ADDITIONAL2' ? DocumentSubCategory.AdditionalDocument2 :
                    DocumentSubCategory.AdditionalDocument3;

    return sub_category;
  }

  onFileSelected(event: Event, docType: any) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;

    this.onUploadDocument({
      file: input.files[0],
      type: docType
    });

    input.value = ''; // allow same file reselect
  }

  async saveDocument(file: any, type: any, filename: any) {
    const loginUserId = localStorage.getItem("loggedUserId");

    const params = {
      "credentialingEventId": localStorage.getItem('credentialingEventId'),
      "categoryId": DocumentCategory.DentalCredential,
      "subCategoryId": this.getSubCategory(type),
      "fileName": filename,
      "isSigned": false,
      "userId": loginUserId || -1
    }
    return await this.dentalCredentialingService.saveDocument(file, params);
  }

  onViewDocument(doc: any) {
    this.dentalCredentialingService.viewAzureDocument(doc.originalFileName.split(".")[0]).subscribe({
      next: (response: any) => {
        const url = window.URL.createObjectURL(response.body);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${doc.name}`;
        link.click();
        window.URL.revokeObjectURL(url);
      }, error: (err: any) => {
        this.sharedService.logError("APIFailed", "FileDental-CredentialsComponent - viewAzureDocument, Method:onViewDocument", this.router.url.split('?')[0], err).subscribe();

      }
    })
  }

  async onDeleteDocument(type: string, index: number) {
    if (this.impersonatedUser) return;
    const loginUserId = localStorage.getItem("loginUserId");
    const credentialingEventId = localStorage.getItem('credentialingEventId');
    const requestParams = {
      "credentialingEventID": Number(credentialingEventId),
      "fileName": this.documentsMap[type][index]['originalFileName']?.split(".")[0],
      "modifiedUserID": loginUserId ? Number(loginUserId) : -1
    }
    this.loaderService.show();
    this.dentalCredentialingService.deleteDocument(requestParams).subscribe({
      next: (response: any) => {
        const documents = JSON.parse(localStorage.getItem('documents') || '[]');
        const subCategoryId = this.getSubCategory(type);
        const _index = documents.findIndex(
          (item: any) => item.subCategoryId === subCategoryId
        );

        if (_index !== -1) {
          documents.splice(_index, 1);
        }
        localStorage.setItem('documents', JSON.stringify(documents));
        this.documentsMap[type].splice(index, 1);
        this.loaderService.hide();
      }, error: (err: any) => {
        this.sharedService.logError("APIFailed", "FileDental-CredentialsComponent - deleteDocument, Method:onDeleteDocument", this.router.url.split('?')[0], err).subscribe();
        this.loaderService.hide();
      }
    })
    return true;
  }

  applyFinalValidations(value: any, type: any, otherValue: any) {

    if (value == 'false' && otherValue == 'false') {
      const control: any = this.dentistCredentialsForm.controls['painManagementNote'];
      control?.setValidators(Validators.required);
      control.updateValueAndValidity({ emitEvent: true });
      control.updateValueAndValidity();
      control?.markAsTouched();
    } else {
      const control: any = this.dentistCredentialsForm.controls['painManagementNote'];
      control.clearValidators();
      control.clearAsyncValidators();
      control.setErrors(null);
      control.updateValueAndValidity();
      control?.markAsUntouched();
    }

    this.dentistCredentialsForm.updateValueAndValidity();
  }

  syncControlledSubstanceValidators() {
    const cdsCtrl = this.dentistCredentialsForm.get('cdsLicenseNumber') as FormControl;
    const cdsExpDateCtrl = this.dentistCredentialsForm.get('cDSExpirationDate') as FormControl;
    const deaCtrl = this.dentistCredentialsForm.get('deaLicenseNumber') as FormControl;
    const deaExpDateCtrl = this.dentistCredentialsForm.get('dEAExpirationDate') as FormControl;

    if (this.cdsForm.value.cds === 'true') {
      cdsCtrl?.setValidators([Validators.required, Validators.pattern(/^[A-Za-z0-9-]{1,10}$/)]);
      cdsExpDateCtrl?.setValidators([Validators.required, minDaysFromSubmissionValidator(this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)]);
      cdsCtrl?.markAsTouched();
      cdsExpDateCtrl?.markAsTouched();
    } else {
      cdsCtrl?.clearValidators();
      cdsCtrl?.setErrors(null);
      cdsCtrl?.markAsUntouched();
      cdsExpDateCtrl?.clearValidators();
      cdsExpDateCtrl?.setErrors(null);
      cdsExpDateCtrl?.markAsUntouched();
    }

    if (this.deaLicenseForm.value.dea === 'true') {
      deaCtrl?.setValidators([Validators.required, Validators.pattern(/^[A-Za-z]{2}[0-9]{7}$/)]);
      deaExpDateCtrl?.setValidators([Validators.required, minDaysFromSubmissionValidator(this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)]);
      deaCtrl?.markAsTouched();
      deaExpDateCtrl?.markAsTouched();
    } else {
      deaCtrl?.clearValidators();
      deaCtrl?.setErrors(null);
      deaCtrl?.markAsUntouched();
      deaExpDateCtrl?.clearValidators();
      deaExpDateCtrl?.setErrors(null);
      deaExpDateCtrl?.markAsUntouched();
    }

    cdsCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    cdsExpDateCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    deaCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    deaExpDateCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });

    this.applyFinalValidations(this.cdsForm.value.cds, 'CDS', this.deaLicenseForm.value.dea);
  }

  onBack() {
    this.processSavingCredentialing('/credentialing/information');
  }

  onCancel() {
    this.processSavingCredentialing('/landing');
  }

  onNext() {
    this.processSavingCredentialing('/credentialing/work-history');
  }

  processSavingCredentialing(toNavigate: any) {
    const formValue = this.dentistCredentialsForm.getRawValue();
    const loginUserId = localStorage.getItem("loggedUserId");
    const isCdsSelected = this.cdsForm.value.cds === 'true';
    const isDeaSelected = this.deaLicenseForm.value.dea === 'true';
    if (!this.sharedService.validateFormLimits(this.dentistCredentialsForm, this.FIELD_LIMITS)) return;

    this.loaderService.show();
    const payload = {
      "credentialId": formValue.credentialID,
      "credentialingEventId": localStorage.getItem('credentialingEventId'),
      "stateLicenseNumber": formValue.stateLicenseNumber,
      "cDSLicenseNumber": isCdsSelected ? formValue.cdsLicenseNumber : null,
      "dEALicenseNumber": isDeaSelected ? formValue.deaLicenseNumber : null,
      "painManagementNote": formValue.painManagementNote,
      "specialtyLicenseNumber": formValue.specialtyLicenseNumber,
      "malpracticeCarrierName": formValue.malpracticeCarrierName,
      "malpracticePolicyHolder": formValue.malpracticePolicyHolder,
      "malpracticeLimitPerClaim": formValue.malpracticeLimitPerClaim ? formValue.malpracticeLimitPerClaim.replace('$', '') : null,
      "malpracticeLimitAggregate": formValue.malpracticeLimitAggregate ? Number(formValue.malpracticeLimitAggregate.replace('$', '')) : null,
      "malpracticeExpirationDate": this.serializeDateForApi(formValue.malpracticeExpirationDate),
      "malpracticePolicyType": this.policyTypeForm.value.malpracticePolicyType,
      "anesthesiaSedationLicense": formValue.anesthesiaSedationLicense,
      "stateLicenseEffectiveDate": this.serializeDateForApi(formValue.stateLicenseEffectiveDate),
      "dEAExpirationDate": isDeaSelected ? this.serializeDateForApi(formValue.dEAExpirationDate) : null,
      "cDSExpirationDate": isCdsSelected ? this.serializeDateForApi(formValue.cDSExpirationDate) : null,
      "specialtyLicenseEffectiveDate": this.serializeDateForApi(formValue.specialtyLicenseEffectiveDate),
      "anesthesiaEffectiveDate": this.serializeDateForApi(formValue.anesthesiaEffectiveDate),
      "userId": loginUserId || -1
    }
    const shouldResignParticipatingAgreement = this.shouldResignParticipatingAgreement(payload);

    this.dentalCredentialingService.saveCredentialing(payload).subscribe({
      next: (response: any) => {
        this.setCredentialsData(payload, response);
        if (shouldResignParticipatingAgreement) {
          this.softDeleteParticipatingAgreementAndPrompt(toNavigate);
          return;
        }
        this.loaderService.hide();
        this.router.navigate([toNavigate]);
      },
      error: (err: any) => {
        this.loaderService.hide();
        this.sharedService.logError("APIFailed", "FileDental-CredentialsComponent - saveCredentialing, Method:processSavingCredentialing", this.router.url.split('?')[0], err).subscribe();
      }
    });

  }

  formatDate(date: any) {
    return this.datePipe.transform(date, 'yyyy-MM-dd');
  }

  validateOnlyPatterns(form: FormGroup): boolean {
    let isValid = true;

    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (!control) return;

      const value = control.value;

      // skip if empty
      if (value === null || value === undefined || value.toString().trim() === '') {
        control.setErrors(null);
        return;
      }

      // check if control has pattern validator
      const errors = control.errors;

      if (errors && errors['pattern']) {
        control.markAsTouched();
        isValid = false;
      }
    });

    return isValid;
  }

  setCredentialsData(request: any, response: any) {
    let credentialData = [{
      "credentialID": response?.CredentialId,
      "credentialingEventId": request?.credentialingEventId,
      "stateLicenseNumber": request?.stateLicenseNumber,
      "cds": this.cdsForm.value.cds,
      "cdsLicenseNumber": request?.cDSLicenseNumber,
      "dea": this.deaLicenseForm.value.dea,
      "deaLicenseNumber": request?.dEALicenseNumber,
      "painManagementNote": request?.painManagementNote,
      "specialtyLicenseNumber": request?.specialtyLicenseNumber,
      "malpracticeCarrierName": request?.malpracticeCarrierName,
      "malpracticePolicyHolder": request?.malpracticePolicyHolder,
      "malpracticeLimitPerClaim": request?.malpracticeLimitPerClaim,
      "malpracticeLimitAggregate": request?.malpracticeLimitAggregate,
      "malpracticeExpirationDate": request?.malpracticeExpirationDate || null,
      "malpracticePolicyType": this.policyTypeForm.value.malpracticePolicyType,
      "anesthesiaSedationLicense": request?.anesthesiaSedationLicense,
      "stateLicenseEffectiveDate": request?.stateLicenseEffectiveDate || null,
      "dEAExpirationDate": request?.dEAExpirationDate || null,
      "cDSExpirationDate": request?.cDSExpirationDate || null,
      "specialtyLicenseEffectiveDate": request?.specialtyLicenseEffectiveDate || null,
      "anesthesiaEffectiveDate": request?.anesthesiaEffectiveDate || null,
      "userId": request?.userId
    }]

    localStorage.setItem('credential', JSON.stringify(credentialData));
    return true;
  }

  private shouldResignParticipatingAgreement(updatedPayload: any): boolean {
    const participatingAgreementDocument = this.getSignedParticipatingAgreementDocument();
    if (!participatingAgreementDocument) {
      return false;
    }

    const credentials = JSON.parse(localStorage.getItem('credential') || '[]');
    const existingCredentials = Array.isArray(credentials) && credentials.length ? credentials[0] : null;
    if (!existingCredentials) {
      return false;
    }

    const normalizeString = (value: any): string => (value ?? '').toString().trim();
    const existingSpecialtyLicenseNumber = normalizeString(existingCredentials?.specialtyLicenseNumber);
    const updatedSpecialtyLicenseNumber = normalizeString(updatedPayload?.specialtyLicenseNumber);
    const existingSpecialtyLicenseEffectiveDate = this.serializeDateForApi(existingCredentials?.specialtyLicenseEffectiveDate);
    const updatedSpecialtyLicenseEffectiveDate = this.serializeDateForApi(updatedPayload?.specialtyLicenseEffectiveDate);

    return existingSpecialtyLicenseNumber !== updatedSpecialtyLicenseNumber
      || existingSpecialtyLicenseEffectiveDate !== updatedSpecialtyLicenseEffectiveDate;
  }

  private getSignedParticipatingAgreementDocument(): any | null {
    const documents = JSON.parse(localStorage.getItem('documents') || '[]');
    if (!Array.isArray(documents)) {
      return null;
    }

    return documents.find((doc: any) => {
      const categoryId = doc?.categoryId ?? doc?.categoryID ?? doc?.CategoryID;
      const subCategoryId = doc?.subCategoryId ?? doc?.subCategoryID ?? doc?.SubCategoryID;
      const isSigned = doc?.isSigned === true;

      return `${categoryId}` === `${DocumentCategory.SignedDocument}`
        && `${subCategoryId}` === `${DocumentSubCategory.ParticipatingAgreement}`
        && isSigned;
    }) || null;
  }

  private softDeleteParticipatingAgreementAndPrompt(toNavigate: string): void {
    const participatingAgreementDocument = this.getSignedParticipatingAgreementDocument();
    if (!participatingAgreementDocument) {
      this.loaderService.hide();
      this.router.navigate([toNavigate]);
      return;
    }

    const loginUserId = localStorage.getItem("loggedUserId");
    const credentialingEventId = localStorage.getItem('credentialingEventId');
    const requestParams = {
      credentialingEventID: Number(credentialingEventId),
      fileName: participatingAgreementDocument.fileName,
      modifiedUserID: loginUserId ? Number(loginUserId) : -1
    };

    this.dentalCredentialingService.deleteDocument(requestParams).subscribe({
      next: () => {
        const documents = JSON.parse(localStorage.getItem('documents') || '[]');
        const updatedDocuments = Array.isArray(documents)
          ? documents.filter((doc: any) => {
            const subCategoryId = doc?.subCategoryId ?? doc?.subCategoryID ?? doc?.SubCategoryID;
            const fileName = doc?.fileName;
            return !(`${subCategoryId}` === `${DocumentSubCategory.ParticipatingAgreement}`
              && `${fileName}` === `${participatingAgreementDocument.fileName}`);
          })
          : [];
        localStorage.setItem('documents', JSON.stringify(updatedDocuments));

        const signedDocuments = JSON.parse(localStorage.getItem('signedDocuments') || '{}');
        if (signedDocuments && typeof signedDocuments === 'object') {
          signedDocuments.participatingAgreement = [];
          localStorage.setItem('signedDocuments', JSON.stringify(signedDocuments));
        }

        this.loaderService.hide();
        this.dialog.open(PopupComponent, {
          disableClose: true,
          data: {
            headerMessage: 'Alert',
            message: this.PARTICIPATING_AGREEMENT_RESIGN_MESSAGE,
            isPTESaved: true
          },
          width: '550px',
          backdropClass: '',
          hasBackdrop: true,
        }).afterClosed().subscribe(() => {
          this.router.navigate([toNavigate]);
        });
      },
      error: (err: any) => {
        this.loaderService.hide();
        this.sharedService.logError("APIFailed", "FileDental-CredentialsComponent - deleteDocument, Method:softDeleteParticipatingAgreementAndPrompt", this.router.url.split('?')[0], err).subscribe();
        this.router.navigate([toNavigate]);
      }
    });
  }

  getCredentialData() {
    const loginUserId = localStorage.getItem("loginUserId");
    this.credentialPayload = JSON.parse(localStorage.getItem('getCredentialpayload')!);
    let getCredentialpayload = {
      "npi": this.credentialPayload['npi'],
      "licenseNumber": this.credentialPayload['licenseNumber'],
      "userId": loginUserId || -1
    }
    this.sharedService.getCredentiallingData(getCredentialpayload).subscribe({
      next: (results: any) => {
        localStorage.setItem('credentialingEvent', JSON.stringify(results['credentialingEvent']));
        localStorage.setItem('credential', JSON.stringify(results['credentials']));
        localStorage.setItem('documents', JSON.stringify(results['documents']));
        localStorage.setItem('credentialingEventId', results['credentialingEvent']['credentialingEventId']);
        this.patchValues(results);
      },
      error: (err) => {
        this.sharedService.logError("APIFailed", "FileMainComponent - getCredentiallingData, Method:getCredentialData", this.router.url.split('?')[0], err).subscribe();
      }
    });
  }

  patchValues(results: any) {
    const userDetails: any = JSON.parse(localStorage.getItem('loggedUserDetails')!);
    if (results?.credentials?.length) {
      const credentials = results.credentials[0];
      const cdsLicenseNumber = credentials?.cdsLicenseNumber ?? credentials?.cDSLicenseNumber ?? '';
      const deaLicenseNumber = credentials?.deaLicenseNumber ?? credentials?.dEALicenseNumber ?? '';
      const cdsExpirationDate = this.normalizeDate(credentials?.cDSExpirationDate);
      const deaExpirationDate = this.normalizeDate(credentials?.dEAExpirationDate);
      const stateLicenseEffectiveDate = this.normalizeDate(credentials?.stateLicenseEffectiveDate);
      const malpracticeExpirationDate = this.normalizeDate(credentials?.malpracticeExpirationDate);
      const specialtyLicenseEffectiveDate = this.normalizeDate(credentials?.specialtyLicenseEffectiveDate);
      const anesthesiaEffectiveDate = this.normalizeDate(credentials?.anesthesiaEffectiveDate);
      const isCdsSelected = credentials?.cds === true || credentials?.cds === 'true' || !!cdsLicenseNumber || !!cdsExpirationDate;
      const isDeaSelected = credentials?.dea === true || credentials?.dea === 'true' || !!deaLicenseNumber || !!deaExpirationDate;

      this.dentistCredentialsForm.patchValue({
        credentialID: credentials?.credentialID,
        stateLicenseNumber: credentials?.stateLicenseNumber || userDetails?.licenseNumber,
        cdsLicenseNumber: cdsLicenseNumber,
        deaLicenseNumber: deaLicenseNumber,
        painManagementNote: credentials?.painManagementNote,
        specialtyLicenseNumber: credentials?.specialtyLicenseNumber,
        anesthesiaSedationLicense: credentials?.anesthesiaSedationLicense,
        malpracticeCarrierName: credentials?.malpracticeCarrierName,
        malpracticePolicyHolder: credentials?.malpracticePolicyHolder,
        malpracticeLimitPerClaim: credentials?.malpracticeLimitPerClaim ? `$${credentials.malpracticeLimitPerClaim}` : null,
        malpracticeLimitAggregate: credentials?.malpracticeLimitAggregate ? `$${credentials.malpracticeLimitAggregate}` : null,
        malpracticeExpirationDate: malpracticeExpirationDate || null,
        stateLicenseEffectiveDate: stateLicenseEffectiveDate || null,
        dEAExpirationDate: deaExpirationDate || null,
        cDSExpirationDate: cdsExpirationDate || null,
        specialtyLicenseEffectiveDate: specialtyLicenseEffectiveDate || null,
        anesthesiaEffectiveDate: anesthesiaEffectiveDate || null,
      });
      if (isCdsSelected) {
        const cdsExpDateCtrl = this.dentistCredentialsForm.get('cDSExpirationDate') as FormControl;
        this.dentistCredentialsForm.get('cdsLicenseNumber')?.setValidators([Validators.required, Validators.pattern(/^[A-Za-z0-9-]{1,10}$/)]);
        this.dentistCredentialsForm.get('cdsLicenseNumber')?.updateValueAndValidity();
        cdsExpDateCtrl?.setValidators([
          Validators.required, minDaysFromSubmissionValidator(this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)
        ]);
        cdsExpDateCtrl.updateValueAndValidity();
        this.cdsForm.patchValue({ cds: 'true' }, { emitEvent: false });
      } else {
        this.cdsForm.patchValue({ cds: 'false' }, { emitEvent: false });
      }
      if (isDeaSelected) {
        const deaExpDateCtrl = this.dentistCredentialsForm.get('dEAExpirationDate') as FormControl;
        this.dentistCredentialsForm.get('deaLicenseNumber')?.setValidators([Validators.required, Validators.pattern(/^[A-Za-z]{2}[0-9]{7}$/)]);
        this.dentistCredentialsForm.get('deaLicenseNumber')?.updateValueAndValidity();
        deaExpDateCtrl?.setValidators([
          Validators.required, minDaysFromSubmissionValidator(this.MIN_EXPIRATION_DAYS_FROM_SUBMISSION)
        ]);
        deaExpDateCtrl.updateValueAndValidity();
        this.deaLicenseForm.patchValue({ dea: 'true' }, { emitEvent: false });
      } else {
        this.deaLicenseForm.patchValue({ dea: 'false' }, { emitEvent: false });
      }
      if (credentials?.specialtyLicenseNumber) {
        this.dentistCredentialsForm.get('specialtyLicenseEffectiveDate')?.setValidators([Validators.required]);
        this.dentistCredentialsForm.get('specialtyLicenseEffectiveDate')?.updateValueAndValidity();
      }
      if (credentials?.anesthesiaSedationLicense) {
        this.dentistCredentialsForm.get('anesthesiaEffectiveDate')?.setValidators([Validators.required]);
        this.dentistCredentialsForm.get('anesthesiaEffectiveDate')?.updateValueAndValidity();
      }
      this.policyTypeForm.patchValue({ malpracticePolicyType: credentials?.malpracticePolicyType });
      this.cdsForm.updateValueAndValidity();
      this.deaLicenseForm.updateValueAndValidity();
    }
    this.documentsMap = {
      STATE_LICENSE: [],
      CDS: [],
      DEA: [],
      MALPRACTICE: [],
      SPECIALTY: [],
      ANESTHESIA: [],
      ADDITIONAL1: [],
      ADDITIONAL2: [],
      ADDITIONAL3: [],
    };
    for (const doc of results['documents']) {
      if (doc.categoryId == DocumentCategory.DentalCredential) {
        this.insertDocumentMap(doc);
      }
    }
    this.dentistCredentialsForm.updateValueAndValidity();
  }

  insertDocumentMap(doc: any) {
    switch (doc.subCategoryId) {
      case DocumentSubCategory.StateLicense:
        this.documentsMap['STATE_LICENSE'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `State_License.pdf`,
        });
        break;

      case DocumentSubCategory.CDSLicense:
        this.documentsMap['CDS'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `CDS_License.pdf`,
        });
        break;

      case DocumentSubCategory.DEALicense:
        this.documentsMap['DEA'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `DEA_License.pdf`,
        });
        break;

      case DocumentSubCategory.MalpracticeInsuranceLicense:
        this.documentsMap['MALPRACTICE'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `Malpractice_Insurance_License.pdf`,
        });
        break;

      case DocumentSubCategory.SpecialtyLicense:
        this.documentsMap['SPECIALTY'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `Speciality_License.pdf`,
        });
        break;

      case DocumentSubCategory.AnesthesiaSedationLicense:
        this.documentsMap['ANESTHESIA'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `Anesthesia_Sedation_License.pdf`,
        });
        break;

      case DocumentSubCategory.AdditionalDocument1:
        this.documentsMap['ADDITIONAL1'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `Additional_Document_1.pdf`,
        });
        break;

      case DocumentSubCategory.AdditionalDocument2:
        this.documentsMap['ADDITIONAL2'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `Additional_Document_2.pdf`,
        });
        break;

      case DocumentSubCategory.AdditionalDocument3:
        this.documentsMap['ADDITIONAL3'].push({
          originalFileName: `${doc.fileName}.pdf`,
          name: `Additional_Document_3.pdf`,
        });
        break;

    }
  }

  async updateFormValidation(e: any, type: 'CDS' | 'DEA') {
    const isValuePresent = e?.target?.value == 'true';
    const isValueFalse = e?.target?.value == 'false';

    const control =
      type === 'CDS'
        ? this.dentistCredentialsForm.get('cdsLicenseNumber') as FormControl
        : this.dentistCredentialsForm.get('deaLicenseNumber') as FormControl;

    if (!control) return;

    if (isValuePresent) {
      control.setValidators([Validators.required]);
      control.updateValueAndValidity();
    } else if (isValueFalse) {
      await this.onDeleteDocument(type, 0);
      control.setValue(null);
      control.clearValidators();
      control.clearAsyncValidators();
      control.setErrors(null);
      control.updateValueAndValidity();
    } else {
      control.clearValidators();
      control.clearAsyncValidators();
      control.setErrors(null);
      control.updateValueAndValidity();
    }
    // 🔑 CRITICAL: onlySelf avoids stale parent state
    control.updateValueAndValidity({ onlySelf: true, emitEvent: true });

    // Update form ONCE
    this.dentistCredentialsForm.updateValueAndValidity({ emitEvent: true });
    this.dentistCredentialsForm.markAllAsTouched();
  }

  get carrierNameCtrl(): FormControl {
    return this.dentistCredentialsForm.get('malpracticeCarrierName') as FormControl;
  }

  get policyHolderCtrl(): FormControl {
    return this.dentistCredentialsForm.get('malpracticePolicyHolder') as FormControl;
  }

  get limitPerClaimCtrl(): FormControl {
    return this.dentistCredentialsForm.get('malpracticeLimitPerClaim') as FormControl;
  }

  get limitAggregateCtrl(): FormControl {
    return this.dentistCredentialsForm.get('malpracticeLimitAggregate') as FormControl;
  }

  get expirationDateCtrl(): FormControl {
    return this.dentistCredentialsForm.get('malpracticeExpirationDate') as FormControl;
  }

  get stateLicenseCtrl(): FormControl {
    return this.dentistCredentialsForm.get('stateLicenseNumber') as FormControl;
  }

  get cdsLicenseCtrl(): FormControl {
    return this.dentistCredentialsForm.get('cdsLicenseNumber') as FormControl;
  }

  get deaLicenseCtrl(): FormControl {
    return this.dentistCredentialsForm.get('deaLicenseNumber') as FormControl;
  }

  get specialityLicenseCtrl(): FormControl {
    return this.dentistCredentialsForm.get('specialtyLicenseNumber') as FormControl;
  }

  get anesthesiaCtrl(): FormControl {
    return this.dentistCredentialsForm.get('anesthesiaSedationLicense') as FormControl;
  }

  get painManagementCtrl(): FormControl {
    return this.dentistCredentialsForm.get('painManagementNote') as FormControl;
  }

  getAction(): any {
    const el = JSON.parse(localStorage.getItem('dentistInfo') || '{}') || {};
    const credentialingEvent = JSON.parse(localStorage.getItem('credentialingEvent') || '{}');
    const eventStatus = credentialingEvent?.eventStatus;

    if (!el?.reCredentialDate) {
      return false;
    }
    const recredDate: Date = this.parseYyyyMmDd("" + el.reCredentialDate);

    if (!recredDate) {
      return false;
    }
    const locations = JSON.parse(localStorage.getItem('whLocations') || '[]');
    const isNonPar =
      locations.length > 0 &&
      locations.every(
        (loc: any) =>
          loc?.providerNetwork?.toLowerCase() === 'not participating'
      );
    if (isNonPar) {
      return false;
    }
    const today = new Date();
    const dueDate = new Date(recredDate);
    const value = Number(this.sharedService.credentialing_graceperiod);
    const gracePeriod = !value || isNaN(value) ? 180 : value;

    dueDate.setDate(dueDate.getDate() - gracePeriod);

    //  Block only if TOO EARLY (more than 180 days before)
    if (today < dueDate) {
      return false;
    }

    return true;
  }

  parseYyyyMmDd(dateString: string): Date {
    const year = Number(dateString.substring(0, 4));
    const month = Number(dateString.substring(4, 6)) - 1;
    const day = Number(dateString.substring(6, 8));

    return new Date(year, month, day);
  }

  get stateLicenseEffectiveDateCtrl(): FormControl {
    return this.dentistCredentialsForm.get('stateLicenseEffectiveDate') as FormControl;
  }

  get dEAExpirationDateCtrl(): FormControl {
    return this.dentistCredentialsForm.get('dEAExpirationDate') as FormControl;
  }

  get cDSExpirationDateCtrl(): FormControl {
    return this.dentistCredentialsForm.get('cDSExpirationDate') as FormControl;
  }

  get specialtyLicenseEffectiveDateCtrl(): FormControl {
    return this.dentistCredentialsForm.get('specialtyLicenseEffectiveDate') as FormControl;
  }

  get anesthesiaEffectiveDateCtrl(): FormControl {
    return this.dentistCredentialsForm.get('anesthesiaEffectiveDate') as FormControl;
  }

  isNotesLimitExceeded(): boolean {
    const notes = this.dentistCredentialsForm.get('painManagementNote')?.value || '';
    return notes.length > 600;
  }

  restrictNonNumeric(event: KeyboardEvent, control: any): void {
    this.sharedService.preventNonNumericForDecimal(event);

    const inputElement = event.target as HTMLInputElement;

    setTimeout(() => {

      // Remove $ before processing
      let value = inputElement.value.replace('$', '');

      // If user starts with '.', convert to '0.'
      if (value.startsWith('.')) {
        value = '0' + value;
      }

      // Prevent multiple decimals
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts[1];
      }

      // Remove leading zeros only if not decimal
      if (/^0\d/.test(value) && !value.startsWith('0.')) {
        value = value.replace(/^0+/, '');
      }

      // Allow numbers with max 2 decimal places
      const validPattern = /^\d*(\.\d{0,2})?$/;

      if (!validPattern.test(value)) {
        value = value.slice(0, -1);
      }

      // Add $ prefix only if value exists
      inputElement.value = value ? `$${value}` : '';

      this.dentistCredentialsForm.controls[control].setValue(
        inputElement.value === '$' ? '' : inputElement.value,
        { emitEvent: false }
      );

    }, 0);
  }

  normalizeDate = (value: any) => {
    if (!value) return '';
    const pad = (num: number) => String(num).padStart(2, '0');

    // ddmo app-date-input can emit Moment objects through Angular Material MomentDateAdapter.
    if (value && typeof value === 'object' && typeof value.format === 'function') {
      return value.format('YYYY-MM-DD');
    }

    if (value instanceof Date && !isNaN(value.getTime())) {
      // Preserve user-selected local dates while also handling UTC-midnight values from API/localStorage.
      const isUtcMidnight =
        value.getUTCHours() === 0 &&
        value.getUTCMinutes() === 0 &&
        value.getUTCSeconds() === 0 &&
        value.getUTCMilliseconds() === 0;

      if (isUtcMidnight) {
        return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;
      }

      return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
    }

    const stringValue = value.toString().trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
      return this.isStrictYyyyMmDd(stringValue) ? stringValue : '';
    }

    // Handle ISO datetime strings safely; splitting on T can shift dates across timezones.
    const isoDateTimeMatch = stringValue.match(
      /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(Z|[+-]\d{2}:?\d{2})?$/
    );
    if (isoDateTimeMatch) {
      const [, datePart, hours, minutes, seconds = '00', timezonePart] = isoDateTimeMatch;
      const isMidnightTime = hours === '00' && minutes === '00' && seconds === '00';

      // Preserve canonical date-only semantics from backend timestamps like 2026-10-12T00:00:00Z.
      if (timezonePart && isMidnightTime) {
        return datePart;
      }

      const parsedIso = new Date(stringValue);
      if (!isNaN(parsedIso.getTime())) {
        return `${parsedIso.getFullYear()}-${pad(parsedIso.getMonth() + 1)}-${pad(parsedIso.getDate())}`;
      }

      return datePart;
    }

    const mmDdYyyyMatch = stringValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmDdYyyyMatch) {
      const [, mm, dd, yyyy] = mmDdYyyyMatch;
      const normalized = `${yyyy}-${pad(Number(mm))}-${pad(Number(dd))}`;
      return this.isStrictYyyyMmDd(normalized) ? normalized : '';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
      return stringValue;
    }

    const isParsedUtcMidnight =
      parsed.getUTCHours() === 0 &&
      parsed.getUTCMinutes() === 0 &&
      parsed.getUTCSeconds() === 0 &&
      parsed.getUTCMilliseconds() === 0;

    if (isParsedUtcMidnight) {
      return `${parsed.getUTCFullYear()}-${pad(parsed.getUTCMonth() + 1)}-${pad(parsed.getUTCDate())}`;
    }

    return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
  };

  private serializeDateForApi(value: any): string | null {
    if (!value) {
      return null;
    }

    const pad = (num: number) => String(num).padStart(2, '0');
    const fromDate = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const fromUtcDate = (date: Date) => `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
    const fromYmdParts = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`;
    const validateAndReturn = (candidate: string | null): string | null => {
      if (!candidate) {
        return null;
      }
      return this.isStrictYyyyMmDd(candidate) ? candidate : null;
    };

    const parseFromDateObject = (date: Date): string | null => {
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return null;
      }

      const isUtcMidnight =
        date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0 &&
        date.getUTCMilliseconds() === 0;

      if (isUtcMidnight) {
        return fromUtcDate(date);
      }

      return fromDate(date);
    };

    const parseDateStringInput = (input: string): string | null => {
      const normalized = input.trim();
      if (!normalized) {
        return null;
      }

      if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
        return validateAndReturn(normalized);
      }

      const mmDdYyyyMatch = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mmDdYyyyMatch) {
        const [, mm, dd, yyyy] = mmDdYyyyMatch;
        return validateAndReturn(`${yyyy}-${pad(Number(mm))}-${pad(Number(dd))}`);
      }

      const yyyymmddMatch = normalized.match(/^(\d{4})(\d{2})(\d{2})$/);
      if (yyyymmddMatch) {
        const [, yyyy, mm, dd] = yyyymmddMatch;
        return validateAndReturn(`${yyyy}-${mm}-${dd}`);
      }

      const isoDateTimeMatch = normalized.match(
        /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(Z|[+-]\d{2}:?\d{2})?$/
      );
      if (isoDateTimeMatch) {
        const [, datePart, hours, minutes, seconds = '00', timezonePart] = isoDateTimeMatch;
        const isMidnightTime = hours === '00' && minutes === '00' && seconds === '00';

        // Preserve canonical backend date-only timestamps and avoid timezone backshift.
        if (timezonePart && isMidnightTime) {
          return datePart;
        }

        const parsedIso = new Date(normalized);
        if (!isNaN(parsedIso.getTime())) {
          return validateAndReturn(fromDate(parsedIso));
        }

        return datePart;
      }

      const parsed = new Date(normalized);
      if (!isNaN(parsed.getTime())) {
        return validateAndReturn(fromDate(parsed));
      }

      return null;
    };

    const parseUnknownInput = (input: any): string | null => {
      if (!input) {
        return null;
      }

      if (input instanceof Date) {
        return parseFromDateObject(input);
      }

      if (typeof input === 'string') {
        return parseDateStringInput(input);
      }

      if (Array.isArray(input) && input.length >= 3) {
        const [yyyy, mmZeroBasedOrOneBased, dd] = input;
        const year = Number(yyyy);
        const monthCandidate = Number(mmZeroBasedOrOneBased);
        const day = Number(dd);

        if (!isNaN(year) && !isNaN(monthCandidate) && !isNaN(day)) {
          // Moment creation arrays are usually [year, month(0-11), day].
          const month = monthCandidate >= 0 && monthCandidate <= 11 ? monthCandidate + 1 : monthCandidate;
          if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            return validateAndReturn(fromYmdParts(year, month, day));
          }
        }
      }

      if (typeof input === 'object') {
        const year = Number(input.year ?? input.y ?? input.YYYY);
        const month = Number(input.month ?? input.m ?? input.MM);
        const day = Number(input.day ?? input.date ?? input.d ?? input.DD);
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
            return validateAndReturn(fromYmdParts(year, month, day));
          }
        }
      }

      return null;
    };

    if (value instanceof Date) {
      return parseFromDateObject(value);
    }

    if (value && typeof value === 'object') {
      // For Moment-like values, read current calendar parts first.
      if (
        typeof value.isValid === 'function' &&
        value.isValid() &&
        typeof value.year === 'function' &&
        typeof value.month === 'function' &&
        typeof value.date === 'function'
      ) {
        const year = Number(value.year());
        const month = Number(value.month()) + 1;
        const day = Number(value.date());
        if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
          return validateAndReturn(fromYmdParts(year, month, day));
        }
      }

      if (typeof value.format === 'function') {
        const formattedValue = value.format('YYYY-MM-DD');
        const normalizedFormattedValue = parseDateStringInput(formattedValue);
        if (normalizedFormattedValue) {
          return validateAndReturn(normalizedFormattedValue);
        }
      }

      if (typeof value.toDate === 'function') {
        const date = value.toDate();
        if (date instanceof Date) {
          const parsedFromToDate = parseFromDateObject(date);
          if (parsedFromToDate) {
            return validateAndReturn(parsedFromToDate);
          }
        }
      }

      // Moment-like object from Material MomentDateAdapter.
      if (value._d instanceof Date) {
        const parsedFromInternalDate = parseFromDateObject(value._d as Date);
        if (parsedFromInternalDate) {
          return validateAndReturn(parsedFromInternalDate);
        }
      }

      // Fallback to raw Moment internals when current date parts are unavailable.
      const rawMomentInput = parseUnknownInput(value._i);
      if (rawMomentInput) {
        return validateAndReturn(rawMomentInput);
      }

      const creationInput = typeof value.creationData === 'function' ? value.creationData()?.input : null;
      const rawCreationInput = parseUnknownInput(creationInput);
      if (rawCreationInput) {
        return validateAndReturn(rawCreationInput);
      }

      const rawArrayInput = parseUnknownInput(value._a);
      if (rawArrayInput) {
        return validateAndReturn(rawArrayInput);
      }

    }

    const stringValue = value.toString().trim();
    return validateAndReturn(parseDateStringInput(stringValue));
  }

  private isStrictYyyyMmDd(value: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return false;
    }

    const [yearString, monthString, dayString] = value.split('-');
    const year = Number(yearString);
    const month = Number(monthString);
    const day = Number(dayString);

    if (!year || month < 1 || month > 12 || day < 1 || day > 31) {
      return false;
    }

    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }

  private hasMeaningfulValue(value: any): boolean {
    return value !== null && value !== undefined && `${value}`.trim() !== '';
  }

}
